import styled from 'styled-components/macro';

import { BigLabel } from '../../SubComponents/Text';
import DefaultDockingLocation from './default-docking-location';
import DockingLocation from './docking-location';
import useTypeSafeLocalize from '../../Hooks/use-type-safe-localize';
import useLazyLoad from '../../Hooks/useLazyLoad';
import { LocalizationCategory, ProductLocalization } from '../../localizations/types';
import {
  DockingLocationOperation,
  GetDockingLocationsQuery,
} from '../../Utils/graphql-sdk';

const FlexibleRoutingContainer = styled.div`
  width: 100%;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
`;

const FlexibleRoutingList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

export type DockingLocationType =
  GetDockingLocationsQuery['getDockingLocationsByShopId'][0];

export interface DockingLocationProps {
  dockingLocation?: DockingLocationType;
  mapLink?: string;
  selected: boolean;
  setSelected: (locationId: string) => void;
  boatCount?: number;
}

const getMapLink = (coordinates?: { lat: number; lng: number }) => {
  if (!coordinates?.lat || !coordinates?.lng) return undefined;

  return `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
};

interface FlexibleRoutingProps {
  pickupLocationId: string | null;
  setPickUpLocation: (locationId: string) => void;
  dropoffLocationId: string | null;
  setDropOffLocation: (locationId: string) => void;
  dockingLocations: DockingLocationType[];
  boatCount: number;
}

const FlexibleRouting = (props: FlexibleRoutingProps) => {
  const localize = useTypeSafeLocalize();
  // Check for images to lazy load when pickupLocations and dropoffLocations changes
  useLazyLoad([props.dockingLocations]);

  const defaultPickUpLocation = props.dockingLocations?.find(
    (location) =>
      location?.default && location?.operations.includes(DockingLocationOperation.Pickup)
  );
  const defaultDropOffLocation = props.dockingLocations?.find(
    (location) =>
      location?.default && location?.operations.includes(DockingLocationOperation.Dropoff)
  );
  const pickupLocations = props.dockingLocations?.filter(
    (location) =>
      !location?.default && location?.operations.includes(DockingLocationOperation.Pickup)
  );
  const dropoffLocations = props.dockingLocations?.filter(
    (location) =>
      !location?.default &&
      location?.operations.includes(DockingLocationOperation.Dropoff)
  );

  const handlePickUpChange = (locationId: string) => {
    props.setPickUpLocation(locationId);

    const dropoffHeading = document.getElementById('dropoffHeading');
    if (dropoffHeading) {
      setTimeout(() => {
        dropoffHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    }
  };

  const handleDropOffChange = (locationId: string) => {
    props.setDropOffLocation(locationId);

    const step1NextButton = document.getElementById('step1NextButton');
    if (step1NextButton) {
      setTimeout(() => {
        step1NextButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 350);
    }
  };

  return (
    <FlexibleRoutingContainer>
      <FlexibleRoutingList>
        <BigLabel>
          {localize(
            LocalizationCategory.product,
            ProductLocalization.selectPickUpLocation
          )}
        </BigLabel>

        <DefaultDockingLocation
          dockingLocation={defaultPickUpLocation}
          mapLink={getMapLink(defaultPickUpLocation?.location)}
          selected={props.pickupLocationId === defaultPickUpLocation?.id}
          setSelected={handlePickUpChange}
        />

        {pickupLocations.length > 0 &&
          pickupLocations.map((location: DockingLocationType) => (
            <DockingLocation
              key={location?.id}
              dockingLocation={location}
              mapLink={getMapLink(location?.location)}
              selected={props.pickupLocationId === location?.id}
              setSelected={handlePickUpChange}
              boatCount={props.boatCount}
            />
          ))}
      </FlexibleRoutingList>

      <FlexibleRoutingList>
        <BigLabel id="dropoffHeading" style={{ scrollMargin: '80px' }}>
          {localize(
            LocalizationCategory.product,
            ProductLocalization.selectDropOffLocation
          )}
        </BigLabel>

        <DefaultDockingLocation
          dockingLocation={defaultDropOffLocation}
          mapLink={getMapLink(defaultDropOffLocation?.location)}
          selected={props.dropoffLocationId === defaultDropOffLocation?.id}
          setSelected={handleDropOffChange}
        />

        {dropoffLocations.length > 0 &&
          dropoffLocations.map((location: DockingLocationType) => (
            <DockingLocation
              key={location?.id}
              dockingLocation={location}
              mapLink={getMapLink(location?.location)}
              selected={props.dropoffLocationId === location?.id}
              setSelected={handleDropOffChange}
              boatCount={props.boatCount}
            />
          ))}
      </FlexibleRoutingList>
    </FlexibleRoutingContainer>
  );
};

export default FlexibleRouting;
